import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './FichierPro.css';

const BASE = process.env.REACT_APP_API_URL || '';
const EM = BASE + '/api/emailing';
const CEE = BASE + '/api/compagne-cee';
const CEE_PUBLIC = 'https://aides.cee-1euro.fr';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const mp = () => ({ headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
const fmt = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return d; } };
const landingUrl = (id) => id === 'confirmation-cee' ? `${CEE_PUBLIC}/confirmation-RDV` : id === 'confirmation-rdv' ? `${CEE_PUBLIC}/confirmation-rdv` : `${CEE_PUBLIC}/p/${id}`;

const CHANNELS = [
  { v: 'brevo_smtp', l: 'Brevo (+ secours SMTP)' },
  { v: 'brevo', l: 'Brevo' },
  { v: 'smtp', l: 'SMTP interne' }
];

const defaultBody = (landing, label) => `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;color:#333;line-height:1.6">
  <p>Bonjour {{first_name}},</p>
  <p>Votre établissement <strong>{{company}}</strong> est éligible au dispositif CEE (Certificats d'Économie d'Énergie).</p>
  <p>Cliquez ci-dessous pour en savoir plus :</p>
  ${landing ? `<p style="text-align:center;margin:28px 0"><a href="{{landing_url}}" style="background:#4f46e5;color:#fff;padding:14px 28px;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block">${label || 'Découvrir mon offre'}</a></p>` : ''}
  <p>Cordialement,</p>
</div>`;

const emptyLanding = () => ({
  title: '', business_name: '', description: '', color_primary: '#4f46e5', color_secondary: '#10b981',
  button_text: 'Envoyer', email_recipients: '',
  fields: [{ name: 'nom', label: 'Nom', type: 'text', required: true }, { name: 'email', label: 'Email', type: 'email', required: true }, { name: 'telephone', label: 'Téléphone', type: 'tel', required: true }]
});

// Badge de statut d'une campagne (gère le mode goutte-à-goutte 'scheduled')
function statusBadge(c) {
  const map = {
    sent: { bg: '#d1fae5', fg: '#065f46', label: 'terminé' },
    sending: { bg: '#fef3c7', fg: '#92400e', label: 'envoi…' },
    scheduled: { bg: '#dbeafe', fg: '#1e40af', label: '⏳ programmé · reprise 9h' },
    failed: { bg: '#fecaca', fg: '#b91c1c', label: 'échec' },
    draft: { bg: '#e5e7eb', fg: '#555', label: 'brouillon' },
  };
  const s = map[c.status] || { bg: '#e5e7eb', fg: '#555', label: c.status };
  return <span style={{ padding: '3px 9px', borderRadius: '12px', fontSize: '12px', background: s.bg, color: s.fg }}>{s.label}</span>;
}

function CompagneCEE() {
  const [section, setSection] = useState('campaigns');
  const [stats, setStats] = useState({ contacts: 0, campaigns: 0, sent: 0, opens: 0 });
  const [quota, setQuota] = useState(null);
  const [quotaErr, setQuotaErr] = useState('');
  const [msg, setMsg] = useState('');
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  // campaigns
  const [campaigns, setCampaigns] = useState([]);
  // results (hot leads)
  const [hot, setHot] = useState([]);
  const [hotSort, setHotSort] = useState('recent');
  const [hotCampaign, setHotCampaign] = useState('');
  const [live, setLive] = useState(true);
  const seenRef = useRef(new Set());
  const hotInitRef = useRef(false);
  const [newHot, setNewHot] = useState(0); // nouvelles ouvertures non vues
  const [lastUpd, setLastUpd] = useState(null);
  // contacts / lists
  const [lists, setLists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [importing, setImporting] = useState(false);
  // Brevo lists / sync
  const [brevoLists, setBrevoLists] = useState([]);
  const [brevoErr, setBrevoErr] = useState('');
  const [syncForm, setSyncForm] = useState(null); // { source, listId }
  const [syncing, setSyncing] = useState(false);
  const [bDetail, setBDetail] = useState(null); // { list, contacts, count, offset, loading }
  const [bSelected, setBSelected] = useState(new Set());
  const [bSearch, setBSearch] = useState('');
  const [bSearchResult, setBSearchResult] = useState(null); // null | {found, inList, contact} | 'searching'
  const [bAddForm, setBAddForm] = useState(null); // formulaire d'ajout manuel
  const [bAdding, setBAdding] = useState(false);
  // Segmentation APE / activité → listes Brevo
  const [seg, setSeg] = useState(null); // { by, segments:[{value,n}], selected:Set, running, results }
  // landing pages
  const [pages, setPages] = useState([]);

  // wizard
  const [wizard, setWizard] = useState(null);
  // détail campagne
  const [detail, setDetail] = useState(null); // { campaign, recipients }
  const [detailLoading, setDetailLoading] = useState(false);
  const [typeChoice, setTypeChoice] = useState(false); // chooser Email / SMS
  const openEmailWizard = () => setWizard({ step: 1, landingMode: 'existing', landingId: '', landingNew: emptyLanding(), audienceMode: 'filter', activites: [], apes: [], list: '', importFile: null, importListName: '', singleEmail: '', singleName: '', singlePhone: '', name: '', subject: 'Votre établissement {{company}} est éligible CEE', body: defaultBody(true, 'Découvrir mon offre'), channel: 'brevo_smtp', from_name: '', from_email: '', reply_to: '', creating: false });
  // SMS
  const [smsForm, setSmsForm] = useState(null);
  const [smsSeg, setSmsSeg] = useState([]);
  const [smsAud, setSmsAud] = useState(null);
  const [smsSaving, setSmsSaving] = useState(false);

  const openSmsForm = () => {
    setSmsForm({ name: '', sender: 'CEE', content: 'Bonjour {{first_name}}, votre etablissement {{company}} est eligible CEE. Rappel possible ? Repondez STOP pour ne plus etre contacte.', activites: [] });
    setSmsAud(null);
    axios.get(`${EM}/segments?by=activite`, auth()).then(r => setSmsSeg(r.data.segments || [])).catch(() => {});
  };
  const toggleSmsAct = (v) => setSmsForm(f => ({ ...f, activites: f.activites.includes(v) ? f.activites.filter(x => x !== v) : [...f.activites, v] }));
  useEffect(() => {
    if (!smsForm) return;
    if (!smsForm.activites.length) { setSmsAud(0); return; }
    let cancel = false;
    axios.post(`${EM}/audience-count`, { activites: smsForm.activites }, auth()).then(r => { if (!cancel) setSmsAud(r.data.count); }).catch(() => {});
    return () => { cancel = true; };
  }, [smsForm]);
  const createSms = async (sendNow) => {
    const f = smsForm;
    if (!f.name.trim() || !f.content.trim()) { alert('Nom et message requis.'); return; }
    if (!f.activites.length) { alert('Choisissez au moins une activité à cibler.'); return; }
    setSmsSaving(true);
    try {
      const r = await axios.post(`${EM}/campaigns`, { type: 'sms', name: f.name, html_body: f.content, sms_sender: f.sender, channel: 'brevo', activites: f.activites }, auth());
      if (sendNow && r.data.id) { await axios.post(`${EM}/campaigns/${r.data.id}/send`, {}, auth()); flash('SMS lancé.'); }
      else flash('SMS enregistré en brouillon.');
      setSmsForm(null); loadCampaigns();
    } catch (e) { alert(e.response?.data?.error || 'Erreur SMS'); } finally { setSmsSaving(false); }
  };

  const openDetail = async (c) => {
    setDetail({ campaign: c, recipients: [] });
    setDetailLoading(true);
    try {
      const [full, rec] = await Promise.all([
        axios.get(`${EM}/campaigns/${c.id}`, auth()),
        axios.get(`${EM}/campaigns/${c.id}/recipients`, auth())
      ]);
      setDetail({ campaign: full.data, recipients: rec.data || [] });
    } catch (e) { /* garde l'aperçu minimal */ }
    finally { setDetailLoading(false); }
  };

  const loadStats = useCallback(async () => { try { setStats((await axios.get(`${EM}/stats`, auth())).data); } catch (e) {} }, []);
  const loadQuota = useCallback(async () => {
    try { setQuota((await axios.get(`${EM}/brevo-quota`, auth())).data); setQuotaErr(''); }
    catch (e) { setQuotaErr(e.response?.data?.error || 'Brevo injoignable'); }
  }, []);
  const loadCampaigns = useCallback(async () => { try { setCampaigns((await axios.get(`${EM}/campaigns`, auth())).data || []); } catch (e) {} }, []);
  const loadLists = useCallback(async () => { try { setLists((await axios.get(`${EM}/lists`, auth())).data.lists || []); } catch (e) {} }, []);
  const loadPages = useCallback(async () => { try { setPages((await axios.get(`${CEE}/pages`, auth())).data || []); } catch (e) {} }, []);
  const loadContacts = useCallback(async () => { try { setContacts((await axios.get(`${EM}/contacts?search=${encodeURIComponent(contactSearch)}&limit=200`, auth())).data.contacts || []); } catch (e) {} }, [contactSearch]);
  const loadBrevoLists = useCallback(async () => {
    try { setBrevoLists((await axios.get(`${EM}/brevo-lists`, auth())).data || []); setBrevoErr(''); }
    catch (e) { setBrevoErr(e.response?.data?.error || 'Brevo injoignable'); }
  }, []);

  const createBrevoList = async () => {
    const name = window.prompt('Nom de la nouvelle liste Brevo :', '');
    if (!name) return;
    try { await axios.post(`${EM}/brevo-lists`, { name }, auth()); await loadBrevoLists(); flash('Liste Brevo créée : ' + name); }
    catch (e) { alert(e.response?.data?.error || 'Erreur création liste Brevo'); }
  };
  const openBrevoList = async (list, offset = 0) => {
    if (offset === 0) { setBSelected(new Set()); setBSearch(''); setBSearchResult(null); }
    setBDetail({ list, contacts: [], count: 0, offset, loading: true });
    try {
      const r = await axios.get(`${EM}/brevo-lists/${list.id}/contacts?limit=50&offset=${offset}`, auth());
      setBDetail({ list, contacts: r.data.contacts || [], count: r.data.count || 0, offset, loading: false });
    } catch (e) { setBDetail({ list, contacts: [], count: 0, offset, loading: false }); alert(e.response?.data?.error || 'Erreur chargement liste'); }
  };
  const toggleSel = (email) => setBSelected(prev => { const n = new Set(prev); n.has(email) ? n.delete(email) : n.add(email); return n; });
  const toggleSelAll = () => setBSelected(prev => {
    const pageEmails = bDetail.contacts.map(c => c.email);
    const allSel = pageEmails.every(e => prev.has(e));
    const n = new Set(prev);
    pageEmails.forEach(e => allSel ? n.delete(e) : n.add(e));
    return n;
  });
  const bulkRemove = async () => {
    const emails = [...bSelected];
    if (!emails.length) return;
    if (!window.confirm(`Retirer ${emails.length} contact(s) de la liste « ${bDetail.list.name} » ?`)) return;
    try {
      await axios.post(`${EM}/brevo-lists/${bDetail.list.id}/remove`, { emails }, auth());
      setBDetail(d => ({ ...d, contacts: d.contacts.filter(c => !bSelected.has(c.email)), count: Math.max(0, d.count - emails.length) }));
      setBSelected(new Set()); loadBrevoLists(); flash(`${emails.length} contact(s) retiré(s).`);
    } catch (e) { alert(e.response?.data?.error || 'Erreur retrait groupé'); }
  };
  const addContactToList = async () => {
    const f = bAddForm;
    if (!f.email || !f.email.includes('@')) { alert('Email valide requis.'); return; }
    setBAdding(true);
    try {
      await axios.post(`${EM}/brevo-lists/${bDetail.list.id}/add-contact`, f, auth());
      setBAddForm(null);
      await openBrevoList(bDetail.list, 0);
      loadBrevoLists(); flash('Contact ajouté à la liste.');
    } catch (e) { alert(e.response?.data?.error || 'Erreur ajout contact'); }
    finally { setBAdding(false); }
  };
  const searchInList = async () => {
    const email = bSearch.trim().toLowerCase();
    if (!email) { setBSearchResult(null); return; }
    setBSearchResult('searching');
    try { setBSearchResult((await axios.get(`${EM}/brevo-lists/${bDetail.list.id}/find?email=${encodeURIComponent(email)}`, auth())).data); }
    catch (e) { setBSearchResult({ found: false }); }
  };
  const renameBrevoList = async () => {
    const name = window.prompt('Nouveau nom de la liste :', bDetail.list.name);
    if (!name || name === bDetail.list.name) return;
    try { await axios.put(`${EM}/brevo-lists/${bDetail.list.id}`, { name }, auth()); setBDetail(d => ({ ...d, list: { ...d.list, name } })); loadBrevoLists(); flash('Liste renommée.'); }
    catch (e) { alert(e.response?.data?.error || 'Erreur renommage'); }
  };
  const deleteBrevoList = async () => {
    if (!window.confirm(`Supprimer la liste Brevo « ${bDetail.list.name} » ? (les contacts ne sont pas supprimés de Brevo, juste la liste)`)) return;
    try { await axios.delete(`${EM}/brevo-lists/${bDetail.list.id}`, auth()); setBDetail(null); loadBrevoLists(); flash('Liste supprimée.'); }
    catch (e) { alert(e.response?.data?.error || 'Erreur suppression'); }
  };
  const removeFromBrevoList = async (email) => {
    if (!window.confirm(`Retirer ${email} de la liste « ${bDetail.list.name} » ?`)) return;
    try {
      await axios.post(`${EM}/brevo-lists/${bDetail.list.id}/remove`, { emails: [email] }, auth());
      setBDetail(d => ({ ...d, contacts: d.contacts.filter(c => c.email !== email), count: Math.max(0, d.count - 1) }));
      loadBrevoLists();
    } catch (e) { alert(e.response?.data?.error || 'Erreur retrait'); }
  };

  const openSegment = async (by) => {
    setSeg({ by, segments: [], selected: new Set(), running: false, results: null, loading: true });
    try {
      const r = await axios.get(`${EM}/segments?by=${by}`, auth());
      setSeg(s => ({ ...s, segments: r.data.segments || [], loading: false }));
    } catch (e) { setSeg(s => ({ ...s, segments: [], loading: false })); }
  };
  const toggleSeg = (v) => setSeg(s => { const n = new Set(s.selected); n.has(v) ? n.delete(v) : n.add(v); return { ...s, selected: n }; });
  const toggleSegAll = () => setSeg(s => {
    const all = s.segments.every(x => s.selected.has(x.value));
    return { ...s, selected: all ? new Set() : new Set(s.segments.map(x => x.value)) };
  });
  const runSegment = async () => {
    const values = [...seg.selected];
    if (!values.length) { alert('Sélectionnez au moins une valeur.'); return; }
    if (!window.confirm(`Créer ${values.length} liste(s) Brevo (une par ${seg.by === 'ape' ? 'code APE' : 'activité'}) et y pousser les contacts ?`)) return;
    setSeg(s => ({ ...s, running: true }));
    try {
      const r = await axios.post(`${EM}/segment-to-brevo`, { by: seg.by, values }, auth());
      setSeg(s => ({ ...s, running: false, results: r.data.results || [] }));
      loadBrevoLists();
      flash(`${(r.data.results || []).length} liste(s) Brevo créée(s).`);
    } catch (e) { setSeg(s => ({ ...s, running: false })); alert(e.response?.data?.error || 'Erreur segmentation'); }
  };

  const runSync = async () => {
    if (!syncForm.listId) { alert('Choisissez la liste Brevo cible.'); return; }
    setSyncing(true);
    try {
      const r = await axios.post(`${EM}/sync-to-brevo`, { listId: Number(syncForm.listId), source: syncForm.source || '__all__' }, auth());
      flash(`${r.data.imported}/${r.data.total} contact(s) envoyés vers Brevo${r.data.errors?.length ? ' (erreurs: ' + r.data.errors.join(', ') + ')' : ''}.`);
      setSyncForm(null); loadBrevoLists();
    } catch (e) { alert(e.response?.data?.error || 'Erreur synchronisation Brevo'); }
    finally { setSyncing(false); }
  };
  const loadHot = useCallback(async () => {
    try {
      const p = new URLSearchParams({ sort: hotSort });
      if (hotCampaign) p.append('campaign_id', hotCampaign);
      const data = (await axios.get(`${EM}/hot-leads?${p}`, auth())).data || [];
      let added = 0;
      data.forEach(h => { const k = h.campaign_id + '-' + h.contact_id; if (!seenRef.current.has(k)) { seenRef.current.add(k); if (hotInitRef.current) added++; } });
      hotInitRef.current = true;
      if (added > 0) setNewHot(n => n + added);
      setHot(data);
      setLastUpd(new Date());
    } catch (e) {}
  }, [hotSort, hotCampaign]);

  useEffect(() => { loadStats(); loadCampaigns(); loadQuota(); const t = setInterval(loadQuota, 60000); return () => clearInterval(t); }, [loadStats, loadCampaigns, loadQuota]);
  useEffect(() => {
    if (section === 'campaigns') loadCampaigns();
    else if (section === 'results') loadHot();
    else if (section === 'contacts') { loadLists(); loadContacts(); loadBrevoLists(); }
    else if (section === 'landings') loadPages();
    // eslint-disable-next-line
  }, [section, hotSort, hotCampaign]);
  // Actualisation EN CONTINU (toutes les 6s), quelle que soit la page ouverte
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => { loadHot(); loadStats(); loadCampaigns(); }, 6000);
    return () => clearInterval(t);
  }, [live, loadHot, loadStats, loadCampaigns]);
  // En ouvrant Résultats, on considère les leads chauds comme « vus »
  useEffect(() => { if (section === 'results') setNewHot(0); }, [section]);

  // ---------- actions ----------
  const sendCampaign = async (c) => {
    if (!window.confirm(`Lancer l'envoi de « ${c.name} » ?\nAudience : ${c.tags_filter ? 'liste ' + c.tags_filter : 'tous les contacts'}.`)) return;
    try { await axios.post(`${EM}/campaigns/${c.id}/send`, {}, auth()); flash('Envoi lancé. Les ouvertures remontent dans Résultats en direct.'); setTimeout(loadCampaigns, 1500); }
    catch (e) { alert(e.response?.data?.error || 'Erreur envoi'); }
  };
  const deleteCampaign = async (id) => { if (!window.confirm('Supprimer cette campagne ?')) return; try { await axios.delete(`${EM}/campaigns/${id}`, auth()); loadCampaigns(); loadStats(); } catch (e) {} };
  const viewResults = (c) => { setHotCampaign(c.id); setSection('results'); };
  const deleteContact = async (id) => { if (!window.confirm('Supprimer ce contact ?')) return; try { await axios.delete(`${EM}/contacts/${id}`, auth()); loadContacts(); loadLists(); loadStats(); } catch (e) {} };
  const deletePage = async (id) => { if (!window.confirm('Supprimer cette landing page ?')) return; try { await axios.delete(`${CEE}/pages/${id}`, auth()); loadPages(); } catch (e) {} };
  const copyLink = (id) => { navigator.clipboard.writeText(landingUrl(id)).then(() => flash('Lien copié : ' + landingUrl(id))); };

  const importContacts = async (file, list) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file); if (list) fd.append('list', list);
      const r = await axios.post(`${EM}/contacts/import`, fd, mp());
      flash(`Import : ${r.data.imported} contacts (${r.data.skipped} ignorés)${list ? ' dans la liste « ' + list + ' »' : ''}.`);
      loadContacts(); loadLists(); loadStats();
    } catch (e) { alert(e.response?.data?.error || 'Erreur import'); } finally { setImporting(false); }
  };

  const NAV = [
    { k: 'campaigns', l: 'Campagnes' }, { k: 'sms', l: '📱 SMS' }, { k: 'results', l: '🔥 Résultats' },
    { k: 'contacts', l: 'Contacts & listes' }, { k: 'landings', l: 'Landing pages' }
  ];

  const stat = (v, l, g) => (
    <div style={{ flex: '1 1 120px', background: '#fff', border: '1px solid #e9e9f2', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: g }} />
      <div><div style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1, color: '#1f2937' }}>{v ?? 0}</div><div style={{ fontSize: '12px', color: '#4b5563', marginTop: '3px' }}>{l}</div></div>
    </div>
  );

  return (
    <div className="fichier-pro">
      <div className="fichier-pro-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2>Compagne CEE — Emailing</h2>
          <p className="fichier-pro-subtitle">Créez une campagne de A à Z et suivez en direct qui ouvre vos emails.</p>
        </div>
        <button className="btn-search" style={{ fontSize: '15px', padding: '12px 20px' }} onClick={() => setTypeChoice(true)}>
          ＋ Nouvelle campagne
        </button>
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '4px 0 16px' }}>
        {stat(stats.contacts, 'Contacts', 'linear-gradient(135deg,#6366f1,#8b5cf6)')}
        {stat(stats.campaigns, 'Campagnes', 'linear-gradient(135deg,#06b6d4,#0891b2)')}
        {stat(stats.sent, 'Emails envoyés', 'linear-gradient(135deg,#10b981,#059669)')}
        {stat(stats.opens, 'Ouvertures', 'linear-gradient(135deg,#f59e0b,#d97706)')}
      </div>

      {/* Quota Brevo en direct */}
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center', background: '#0f172a', color: '#fff', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
        <span style={{ fontWeight: 700, fontSize: '13px', letterSpacing: '0.5px' }}>📨 BREVO {quota ? `· ${quota.plan_type}` : ''}</span>
        {quotaErr ? (
          <span style={{ color: '#fca5a5', fontSize: '13px' }}>⚠️ {quotaErr}</span>
        ) : quota ? (
          <>
            <span style={{ fontSize: '14px' }}>Emails restants : <strong style={{ color: quota.emails_remaining > 50 ? '#86efac' : '#fca5a5', fontSize: '18px' }}>{(quota.emails_remaining ?? 0).toLocaleString('fr-FR')}</strong></span>
            <span style={{ fontSize: '14px' }}>Contacts Brevo : <strong style={{ color: '#93c5fd', fontSize: '18px' }}>{(quota.contacts ?? 0).toLocaleString('fr-FR')}</strong></span>
            {quota.plan_type === 'Free' && <span style={{ fontSize: '12px', color: '#94a3b8' }}>(limite gratuite 300/jour)</span>}
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>{quota.account_email}</span>
          </>
        ) : <span style={{ fontSize: '13px', color: '#94a3b8' }}>Chargement du quota…</span>}
        <button className="btn-search" style={{ marginLeft: 'auto', background: '#1e293b', padding: '6px 12px', fontSize: '12px' }} onClick={loadQuota}>🔄 Actualiser</button>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
        {NAV.map(n => <button key={n.k} className="btn-search" onClick={() => setSection(n.k)} style={{ background: section === n.k ? '#6366f1' : '#eef2ff', color: section === n.k ? '#fff' : '#4338ca', position: 'relative' }}>
          {n.l}{n.k === 'results' && newHot > 0 && <span style={{ marginLeft: '6px', background: '#dc2626', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>{newHot}</span>}
        </button>)}
      </div>

      {msg && <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' }}>{msg}</div>}

      {/* Alerte temps réel : nouveaux leads chauds */}
      {newHot > 0 && section !== 'results' && (
        <div onClick={() => setSection('results')} style={{ cursor: 'pointer', background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', padding: '12px 16px', borderRadius: '10px', marginBottom: '12px', fontSize: '15px', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>🔥 {newHot} nouvelle(s) ouverture(s) — un prospect vient de lire ton email !</span>
          <span style={{ background: '#ea580c', color: '#fff', padding: '6px 12px', borderRadius: '8px', fontSize: '13px' }}>Voir les leads chauds →</span>
        </div>
      )}

      {/* ============ CAMPAGNES ============ */}
      {section === 'campaigns' && (() => { const emailCampaigns = campaigns.filter(c => c.type !== 'sms'); return (
        <>
        <div style={{ marginBottom: '12px', fontSize: '13px', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '8px' }}>
          💧 <strong>Envoi en goutte-à-goutte automatique.</strong> Lancez une campagne de n'importe quelle taille (ex. 10 000 mails) :
          le CRM envoie chaque jour le maximum autorisé par Brevo (quota actuel : <strong>{quota ? quota.email_remaining : '…'}</strong> mails),
          puis reprend tout seul <strong>chaque matin à 9h</strong> jusqu'à épuisement de la liste. La campagne reste « ⏳ programmé » entre deux envois.
        </div>
        {emailCampaigns.length ? (
          <div className="table-wrapper">
            <table className="fichier-pro-table">
              <thead><tr><th>Campagne</th><th>Audience</th><th>Statut</th><th>Envoyés</th><th>Ouvertures</th><th>Actions</th></tr></thead>
              <tbody>
                {emailCampaigns.map(c => (
                  <tr key={c.id}>
                    <td className="td-nom">
                      <button onClick={() => openDetail(c)} style={{ background: 'none', border: 'none', padding: 0, color: '#4338ca', fontWeight: 600, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' }}>{c.name}</button>
                      <br /><small style={{ color: '#999' }}>{c.subject}</small>
                    </td>
                    <td>{(() => {
                      let af = []; try { af = JSON.parse(c.activites_filter || '[]'); } catch (e) {}
                      let pf = []; try { pf = JSON.parse(c.apes_filter || '[]'); } catch (e) {}
                      const all = [...af, ...pf];
                      if (all.length) return <span style={{ background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '10px', fontSize: '12px' }} title={all.join(', ')}>{all.length} ciblage(s)</span>;
                      return c.tags_filter ? <span style={{ background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '10px', fontSize: '12px' }}>{c.tags_filter}</span> : 'Tous';
                    })()}</td>
                    <td>{statusBadge(c)}</td>
                    <td>{c.sent_count}/{c.total_contacts}{c.failed_count ? <small style={{ color: '#dc2626' }}> ({c.failed_count} échecs)</small> : ''}</td>
                    <td><strong style={{ color: '#d97706' }}>{c.open_count}</strong></td>
                    <td>
                      <button className="btn-voir-fiche" onClick={() => viewResults(c)}>Résultats</button>{' '}
                      {c.status !== 'sending' && <button className="btn-voir-fiche" style={{ background: '#10b981' }} onClick={() => sendCampaign(c)}>{c.status === 'sent' ? 'Renvoyer' : c.status === 'scheduled' ? 'Reprendre' : 'Envoyer'}</button>}{' '}
                      <button className="btn-voir-fiche" style={{ background: '#dc2626' }} onClick={() => deleteCampaign(c.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="no-results">Aucune campagne. Cliquez sur « ＋ Nouvelle campagne » pour démarrer.</div>}
        </>
      ); })()}

      {/* ============ SMS ============ */}
      {section === 'sms' && (() => { const smsCampaigns = campaigns.filter(c => c.type === 'sms'); return (
        <div>
          <div style={{ marginBottom: '14px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn-search" onClick={openSmsForm}>＋ Nouveau SMS</button>
            <span style={{ fontSize: '13px', color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: '8px' }}>
              ⚠️ Le SMS est payant chez Brevo — crédits SMS : <strong>{quota ? quota.sms_remaining : '…'}</strong>. Recharge sur Brevo pour envoyer.
            </span>
          </div>
          {smsCampaigns.length ? (
            <div className="table-wrapper">
              <table className="fichier-pro-table">
                <thead><tr><th>SMS</th><th>Ciblage</th><th>Statut</th><th>Envoyés</th><th>Actions</th></tr></thead>
                <tbody>
                  {smsCampaigns.map(c => {
                    let af = []; try { af = JSON.parse(c.activites_filter || '[]'); } catch (e) {}
                    return (
                      <tr key={c.id}>
                        <td className="td-nom">{c.name}<br /><small style={{ color: '#999', whiteSpace: 'normal' }}>{(c.html_body || '').slice(0, 70)}</small></td>
                        <td>{af.length ? <span style={{ background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '10px', fontSize: '12px' }} title={af.join(', ')}>{af.length} activité(s)</span> : 'Tous'}</td>
                        <td>{statusBadge(c)}</td>
                        <td>{c.sent_count}/{c.total_contacts}{c.failed_count ? <small style={{ color: '#dc2626' }}> ({c.failed_count} échecs)</small> : ''}</td>
                        <td>
                          {c.status !== 'sending' && <button className="btn-voir-fiche" style={{ background: '#10b981' }} onClick={() => sendCampaign(c)}>{c.status === 'sent' ? 'Renvoyer' : c.status === 'scheduled' ? 'Reprendre' : 'Envoyer'}</button>}{' '}
                          <button className="btn-voir-fiche" style={{ background: '#dc2626' }} onClick={() => deleteCampaign(c.id)}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <div className="no-results">Aucun SMS. Clique sur « ＋ Nouveau SMS ».</div>}
        </div>
      ); })()}

      {/* ============ RÉSULTATS (leads chauds) ============ */}
      {section === 'results' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
            <select className="statut-select" value={hotSort} onChange={e => setHotSort(e.target.value)}>
              <option value="recent">Dernières ouvertures</option>
              <option value="frequent">Plus fréquents (à chaud)</option>
            </select>
            <select className="statut-select" value={hotCampaign} onChange={e => setHotCampaign(e.target.value)}>
              <option value="">Toutes les campagnes</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}><input type="checkbox" checked={live} onChange={e => setLive(e.target.checked)} /> En direct {live && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />}</label>
            <button className="btn-search" onClick={loadHot}>Rafraîchir</button>
            <span style={{ color: '#888', fontSize: '13px' }}>{hot.length} ouvreur(s)</span>
            {lastUpd && <span style={{ color: '#16a34a', fontSize: '12px' }}>● Mis à jour à {lastUpd.toLocaleTimeString('fr-FR')}</span>}
          </div>
          {hot.length ? (
            <div className="table-wrapper">
              <table className="fichier-pro-table">
                <thead><tr><th>Établissement</th><th>Dirigeant</th><th>Téléphone</th><th>Email</th><th>Ville</th><th>Ouvertures</th><th>Dernière</th></tr></thead>
                <tbody>
                  {hot.map((h, i) => (
                    <tr key={h.campaign_id + '-' + h.contact_id} style={i < 3 ? { background: '#fff7ed' } : undefined}>
                      <td className="td-nom">{h.company || '—'}{h.siret ? <><br /><small style={{ color: '#aaa' }}>{h.siret}</small></> : ''}</td>
                      <td>{h.dirigeant || '—'}</td>
                      <td className="td-phone">{h.phone ? <a href={`tel:${h.phone}`} className="phone-link">{h.phone}</a> : '—'}</td>
                      <td>{h.email ? <a href={`mailto:${h.email}`} className="web-link">{h.email}</a> : '—'}</td>
                      <td>{h.city || '—'}</td>
                      <td><span style={{ background: h.open_count >= 3 ? '#fecaca' : '#fed7aa', color: h.open_count >= 3 ? '#b91c1c' : '#9a3412', padding: '3px 10px', borderRadius: '12px', fontWeight: 700 }}>{h.open_count}×</span></td>
                      <td>{fmt(h.last_open_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="no-results">Aucune ouverture pour l'instant. Dès qu'un contact ouvre votre email, il apparaît ici en direct (les plus chauds en haut).</div>}
        </div>
      )}

      {/* ============ CONTACTS & LISTES ============ */}
      {section === 'contacts' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
            <input className="filter-input" style={{ maxWidth: '260px' }} value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Rechercher (email, société, tél…)" onKeyDown={e => e.key === 'Enter' && loadContacts()} />
            <button className="btn-search" onClick={loadContacts}>Rechercher</button>
            <label className="btn-search" style={{ background: '#10b981', cursor: 'pointer', margin: 0 }}>
              {importing ? 'Import…' : '⬆ Importer CSV'}
              <input type="file" accept=".csv" style={{ display: 'none' }} disabled={importing}
                onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) { const n = window.prompt('Nom de la liste pour ces contacts (ex: cee-restaurants) — laisser vide pour aucune :', ''); importContacts(f, n || ''); } }} />
            </label>
          </div>
          {lists.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <strong style={{ fontSize: '13px', color: '#555' }}>Listes :</strong>{' '}
              {lists.map(l => <span key={l.list} style={{ display: 'inline-block', background: '#eef2ff', color: '#4338ca', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', margin: '0 6px 6px 0' }}>{l.list} ({l.n})</span>)}
            </div>
          )}
          {/* Listes Brevo — gestion directe via API */}
          <div style={{ background: '#0f172a', color: '#fff', borderRadius: '12px', padding: '12px 16px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ fontSize: '13px' }}>📨 Listes Brevo (envoi)</strong>
              {brevoErr ? <span style={{ color: '#fca5a5', fontSize: '13px' }}>⚠️ {brevoErr}</span> :
                brevoLists.length ? brevoLists.map(l => <button key={l.id} onClick={() => openBrevoList(l)} title="Voir / modifier" style={{ background: '#1e293b', color: '#93c5fd', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', border: 'none', cursor: 'pointer' }}>{l.name} ({(l.subscribers ?? 0).toLocaleString('fr-FR')}) ✏️</button>)
                  : <span style={{ color: '#94a3b8', fontSize: '13px' }}>Aucune liste</span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                <button className="btn-search" style={{ background: '#1e293b', fontSize: '12px', padding: '6px 12px' }} onClick={createBrevoList}>＋ Créer une liste Brevo</button>
                <button className="btn-search" style={{ background: '#2563eb', fontSize: '12px', padding: '6px 12px' }} onClick={() => { loadBrevoLists(); setSyncForm({ source: '__all__', listId: '' }); }}>⤴ Pousser mes contacts</button>
                <button className="btn-search" style={{ background: '#7c3aed', fontSize: '12px', padding: '6px 12px' }} onClick={() => openSegment('ape')}>🏷️ Listes par APE</button>
                <button className="btn-search" style={{ background: '#7c3aed', fontSize: '12px', padding: '6px 12px' }} onClick={() => openSegment('activite')}>🏷️ Listes par activité</button>
              </div>
            </div>
            {syncForm && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', background: '#1e293b', padding: '10px', borderRadius: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', color: '#94a3b8' }}>Contacts à envoyer</label>
                  <select className="filter-input" style={{ minWidth: '180px' }} value={syncForm.source} onChange={e => setSyncForm({ ...syncForm, source: e.target.value })}>
                    <option value="__all__">Tous mes contacts</option>
                    {lists.map(l => <option key={l.list} value={l.list}>Liste locale : {l.list} ({l.n})</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', color: '#94a3b8' }}>Vers la liste Brevo</label>
                  <select className="filter-input" style={{ minWidth: '180px' }} value={syncForm.listId} onChange={e => setSyncForm({ ...syncForm, listId: e.target.value })}>
                    <option value="">— Choisir —</option>
                    {brevoLists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <button className="btn-search" style={{ background: '#10b981' }} onClick={runSync} disabled={syncing}>{syncing ? 'Envoi…' : 'Lancer la synchro'}</button>
                <button className="btn-close" style={{ position: 'static', fontSize: '13px', padding: '7px 12px' }} onClick={() => setSyncForm(null)} disabled={syncing}>Annuler</button>
              </div>
            )}
          </div>
          <p style={{ fontSize: '12px', color: '#888', marginTop: 0 }}>Colonnes CSV : email, prenom, nom, societe, tel, siret, adresse, ville, cp, categorie (activité), ape/naf, dirigeant, tags. ⚠️ Obligatoires : email + téléphone + un nom (societe OU dirigeant) — sinon la ligne est ignorée.</p>
          {contacts.length ? (
            <div className="table-wrapper">
              <table className="fichier-pro-table">
                <thead><tr><th>Email</th><th>Établissement</th><th>Dirigeant</th><th>Téléphone</th><th>Ville</th><th>Liste</th><th></th></tr></thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id}>
                      <td>{c.email}</td><td className="td-nom">{c.company || '—'}</td><td>{c.dirigeant || '—'}</td>
                      <td className="td-phone">{c.phone ? <a href={`tel:${c.phone}`} className="phone-link">{c.phone}</a> : '—'}</td>
                      <td>{c.city || '—'}</td><td>{c.tags || '—'}</td>
                      <td><button className="btn-voir-fiche" style={{ background: '#dc2626' }} onClick={() => deleteContact(c.id)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="no-results">Aucun contact. Importez un fichier CSV pour constituer une liste.</div>}
        </div>
      )}

      {/* ============ LANDING PAGES ============ */}
      {section === 'landings' && (
        pages.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: '14px' }}>
            {pages.map(p => (
              <div key={p.id} style={{ border: '1px solid #e9e9f2', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                <div style={{ background: `linear-gradient(135deg,${p.color_primary},${p.color_secondary})`, color: '#fff', padding: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px' }}>{p.title}</h3><span style={{ fontSize: '12px', opacity: 0.9 }}>{p.business_name || ''}</span>
                </div>
                <div style={{ padding: '10px 14px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <a href={landingUrl(p.id)} target="_blank" rel="noopener noreferrer" className="btn-voir-fiche" style={{ textDecoration: 'none' }}>Voir</a>
                  <button className="btn-voir-fiche" onClick={() => copyLink(p.id)}>Copier lien</button>
                  <button className="btn-voir-fiche" style={{ background: '#dc2626' }} onClick={() => deletePage(p.id)}>Suppr.</button>
                </div>
              </div>
            ))}
          </div>
        ) : <div className="no-results">Aucune landing page. Créez-en une depuis l'assistant de campagne.</div>
      )}

      {/* ============ DÉTAIL CAMPAGNE ============ */}
      {detail && (() => {
        const c = detail.campaign; const recs = detail.recipients;
        const openedN = recs.filter(r => r.opened).length;
        return (
          <div className="modal-overlay" onClick={() => setDetail(null)}>
            <div className="modal-content planning-modal" style={{ maxWidth: '900px', width: '95vw', maxHeight: '94vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header"><h3 style={{ margin: 0, color: '#111827' }}>{c.name}</h3><button className="btn-close" onClick={() => setDetail(null)}>&times;</button></div>
              <div style={{ padding: '16px', color: '#1f2937' }}>
                {/* Infos */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '10px', marginBottom: '16px' }}>
                  {[['Sujet', c.subject], ['Statut', c.status], ['Canal', c.channel], ['Audience', c.tags_filter || 'Tous les contacts'],
                    ['Expéditeur', `${c.from_name || ''} ${c.from_email ? '<' + c.from_email + '>' : '(défaut)'}`],
                    ['Destinataires', c.total_contacts], ['Envoyés', c.sent_count], ['Échecs', c.failed_count], ['Ouvertures', `${openedN} contact(s)`]
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 10px' }}>
                      <div style={{ fontSize: '11px', color: '#4b5563', textTransform: 'uppercase', fontWeight: 600 }}>{k}</div>
                      <div style={{ fontSize: '14px', fontWeight: 600, wordBreak: 'break-word', color: '#111827' }}>{v}</div>
                    </div>
                  ))}
                </div>
                {c.landing_url && <p style={{ fontSize: '13px', margin: '0 0 14px', color: '#1f2937' }}>Landing : <a href={c.landing_url} target="_blank" rel="noopener noreferrer" className="web-link">{c.landing_url}</a></p>}

                {/* Aperçu du message */}
                <div style={{ marginBottom: '16px' }}>
                  <strong style={{ fontSize: '15px', color: '#111827' }}>Message envoyé</strong>
                  <div style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0' }}>Aperçu (les variables {'{{...}}'} sont remplacées par les infos de chaque contact à l'envoi).</div>
                  {c.html_body
                    ? <iframe title="aperçu" srcDoc={c.html_body} style={{ width: '100%', height: '340px', border: '1px solid #e3e3ef', borderRadius: '8px', background: '#fff' }} />
                    : <div className="no-results">Aperçu indisponible</div>}
                </div>

                {/* Destinataires */}
                <strong style={{ fontSize: '15px', color: '#111827' }}>Destinataires {detailLoading ? '(chargement…)' : `(${recs.length})`}</strong>
                {recs.length ? (
                  <div className="table-wrapper" style={{ marginTop: '8px' }}>
                    <table className="fichier-pro-table">
                      <thead><tr><th>Email</th><th>Établissement</th><th>Téléphone</th><th>Statut</th><th>Ouvert</th><th>Détail</th></tr></thead>
                      <tbody>
                        {recs.map(r => (
                          <tr key={r.contact_id} style={r.opened ? { background: '#fff7ed' } : undefined}>
                            <td>{r.email}</td>
                            <td className="td-nom">{r.company || '—'}{r.dirigeant ? <><br /><small style={{ color: '#aaa' }}>{r.dirigeant}</small></> : ''}</td>
                            <td className="td-phone">{r.phone ? <a href={`tel:${r.phone}`} className="phone-link">{r.phone}</a> : '—'}</td>
                            <td><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px', background: r.status === 'sent' ? '#d1fae5' : '#fecaca', color: r.status === 'sent' ? '#065f46' : '#b91c1c' }}>{r.status === 'sent' ? 'Envoyé' : 'Échec'}</span></td>
                            <td>{r.opened ? <span style={{ background: '#fed7aa', color: '#9a3412', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>✓ {r.open_count}× — {fmt(r.last_open_at)}</span> : <span style={{ color: '#bbb' }}>—</span>}</td>
                            <td><small style={{ color: r.error_message ? '#dc2626' : '#999' }}>{r.error_message ? r.error_message.slice(0, 60) : fmt(r.sent_at)}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : !detailLoading && <div className="no-results">Aucun destinataire (campagne pas encore envoyée).</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ============ DÉTAIL / ÉDITION LISTE BREVO ============ */}
      {bDetail && (
        <div className="modal-overlay" onClick={() => setBDetail(null)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '820px', width: '95vw', maxHeight: '94vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, color: '#111827' }}>📨 {bDetail.list.name} <small style={{ color: '#6b7280', fontWeight: 400 }}>— {bDetail.count.toLocaleString('fr-FR')} contacts</small></h3>
              <button className="btn-close" onClick={() => setBDetail(null)}>&times;</button>
            </div>
            <div style={{ padding: '16px', color: '#1f2937' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <button className="btn-search" onClick={renameBrevoList}>✏️ Renommer</button>
                <button className="btn-search" style={{ background: '#dc2626' }} onClick={deleteBrevoList}>🗑️ Supprimer la liste</button>
                <button className="btn-search" style={{ background: '#2563eb' }} onClick={() => { setBDetail(null); setSyncForm({ source: '__all__', listId: bDetail.list.id }); }}>⤴ Importer mes contacts</button>
                <button className="btn-search" style={{ background: '#0d9488' }} onClick={() => setBAddForm({ email: '', first_name: '', last_name: '', company: '', city: '', zipcode: '', phone: '', siret: '', dirigeant: '', ape: '', category: '' })}>➕ Ajouter un contact</button>
              </div>

              {/* Formulaire d'ajout manuel */}
              {bAddForm && (
                <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#065f46', marginBottom: '8px', fontWeight: 600 }}>Obligatoires : email + téléphone + un nom (établissement OU dirigeant)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: '8px' }}>
                    <input className="filter-input" placeholder="Email *" value={bAddForm.email} onChange={e => setBAddForm({ ...bAddForm, email: e.target.value })} autoFocus />
                    <input className="filter-input" placeholder="Établissement / société *" value={bAddForm.company} onChange={e => setBAddForm({ ...bAddForm, company: e.target.value })} />
                    <input className="filter-input" placeholder="Dirigeant (ou établissement)" value={bAddForm.dirigeant} onChange={e => setBAddForm({ ...bAddForm, dirigeant: e.target.value })} />
                    <input className="filter-input" placeholder="Prénom" value={bAddForm.first_name} onChange={e => setBAddForm({ ...bAddForm, first_name: e.target.value })} />
                    <input className="filter-input" placeholder="Nom" value={bAddForm.last_name} onChange={e => setBAddForm({ ...bAddForm, last_name: e.target.value })} />
                    <input className="filter-input" placeholder="Téléphone *" value={bAddForm.phone} onChange={e => setBAddForm({ ...bAddForm, phone: e.target.value })} />
                    <input className="filter-input" placeholder="Ville" value={bAddForm.city} onChange={e => setBAddForm({ ...bAddForm, city: e.target.value })} />
                    <input className="filter-input" placeholder="Code postal" value={bAddForm.zipcode} onChange={e => setBAddForm({ ...bAddForm, zipcode: e.target.value })} />
                    <input className="filter-input" placeholder="SIRET" value={bAddForm.siret} onChange={e => setBAddForm({ ...bAddForm, siret: e.target.value })} />
                    <input className="filter-input" placeholder="Code APE / NAF" value={bAddForm.ape} onChange={e => setBAddForm({ ...bAddForm, ape: e.target.value })} />
                    <input className="filter-input" placeholder="Activité / secteur" value={bAddForm.category} onChange={e => setBAddForm({ ...bAddForm, category: e.target.value })} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <button className="btn-close" style={{ position: 'static', fontSize: '13px', padding: '7px 12px' }} onClick={() => setBAddForm(null)} disabled={bAdding}>Annuler</button>
                    <button className="btn-search" style={{ background: '#0d9488' }} onClick={addContactToList} disabled={bAdding}>{bAdding ? 'Ajout…' : 'Ajouter à la liste'}</button>
                  </div>
                </div>
              )}

              {/* Recherche + action groupée */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                <input className="filter-input" style={{ maxWidth: '280px' }} value={bSearch} onChange={e => setBSearch(e.target.value)} placeholder="Rechercher un email exact dans la liste…" onKeyDown={e => e.key === 'Enter' && searchInList()} />
                <button className="btn-search" onClick={searchInList}>Rechercher</button>
                {bSearchResult && <button className="btn-close" style={{ position: 'static', fontSize: '13px', padding: '7px 12px' }} onClick={() => { setBSearch(''); setBSearchResult(null); }}>Effacer</button>}
                {bSelected.size > 0 && <button className="btn-search" style={{ background: '#dc2626', marginLeft: 'auto' }} onClick={bulkRemove}>🗑️ Retirer la sélection ({bSelected.size})</button>}
              </div>

              {/* Résultat de recherche */}
              {bSearchResult && bSearchResult !== 'searching' && (
                <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                  {!bSearchResult.found ? <span>Aucun contact « {bSearch} » dans Brevo.</span>
                    : bSearchResult.inList ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                        <span>✅ <strong>{bSearchResult.contact.email}</strong> {(bSearchResult.contact.attributes?.COMPANY || bSearchResult.contact.attributes?.FIRM) ? '— ' + (bSearchResult.contact.attributes.COMPANY || bSearchResult.contact.attributes.FIRM) : ''} est dans cette liste.</span>
                        <button className="btn-voir-fiche" style={{ background: '#dc2626' }} onClick={() => { removeFromBrevoList(bSearchResult.contact.email); setBSearchResult(null); setBSearch(''); }}>Retirer</button>
                      </div>
                    ) : <span>⚠️ <strong>{bSearchResult.contact.email}</strong> existe dans Brevo mais <strong>n'est pas</strong> dans cette liste.</span>}
                </div>
              )}
              {bSearchResult === 'searching' && <div className="no-results">Recherche…</div>}

              {bDetail.loading ? <div className="no-results">Chargement…</div> : bDetail.contacts.length ? (
                <>
                  <div className="table-wrapper">
                    <table className="fichier-pro-table">
                      <thead><tr>
                        <th style={{ width: '30px' }}><input type="checkbox" checked={bDetail.contacts.length > 0 && bDetail.contacts.every(c => bSelected.has(c.email))} onChange={toggleSelAll} title="Tout sélectionner (page)" /></th>
                        <th>Email</th><th>Établissement</th><th>Nom</th><th>Ville</th><th></th></tr></thead>
                      <tbody>
                        {bDetail.contacts.map(c => {
                          const a = c.attributes || {};
                          return (
                            <tr key={c.id} style={bSelected.has(c.email) ? { background: '#eef2ff' } : undefined}>
                              <td><input type="checkbox" checked={bSelected.has(c.email)} onChange={() => toggleSel(c.email)} /></td>
                              <td>{c.email}</td>
                              <td className="td-nom">{a.COMPANY || a.FIRM || a.SOCIETE || '—'}</td>
                              <td>{a.DIRIGEANT || a.NOM || a.LASTNAME || ((a.FIRSTNAME || '') + ' ' + (a.LASTNAME || '')).trim() || '—'}</td>
                              <td>{a.VILLE || a.CITY || '—'}</td>
                              <td><button className="btn-voir-fiche" style={{ background: '#dc2626' }} onClick={() => removeFromBrevoList(c.email)} title="Retirer de la liste">✕</button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {bDetail.count > 50 && (
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', marginTop: '12px' }}>
                      <button className="btn-search" disabled={bDetail.offset === 0} onClick={() => openBrevoList(bDetail.list, Math.max(0, bDetail.offset - 50))}>← Précédent</button>
                      <span style={{ fontSize: '13px', color: '#555' }}>{bDetail.offset + 1}–{Math.min(bDetail.offset + 50, bDetail.count)} / {bDetail.count.toLocaleString('fr-FR')}</span>
                      <button className="btn-search" disabled={bDetail.offset + 50 >= bDetail.count} onClick={() => openBrevoList(bDetail.list, bDetail.offset + 50)}>Suivant →</button>
                    </div>
                  )}
                </>
              ) : <div className="no-results">Liste vide.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ============ SEGMENTATION APE / ACTIVITÉ → LISTES BREVO ============ */}
      {seg && (
        <div className="modal-overlay" onClick={() => !seg.running && setSeg(null)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '620px', width: '94vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 style={{ margin: 0, color: '#111827' }}>Créer des listes Brevo par {seg.by === 'ape' ? 'code APE' : 'activité'}</h3><button className="btn-close" onClick={() => !seg.running && setSeg(null)}>&times;</button></div>
            <div style={{ padding: '16px', color: '#1f2937' }}>
              {seg.results ? (
                <div>
                  <p style={{ marginTop: 0 }}>✅ {seg.results.length} liste(s) créée(s) :</p>
                  <div className="table-wrapper"><table className="fichier-pro-table">
                    <thead><tr><th>{seg.by === 'ape' ? 'APE' : 'Activité'}</th><th>Liste Brevo</th><th>Contacts</th></tr></thead>
                    <tbody>{seg.results.map((r, i) => <tr key={i}><td>{r.value}</td><td className="td-nom">{r.list || '—'}</td><td>{r.error ? <span style={{ color: '#dc2626' }}>{r.error}</span> : <strong>{r.count}</strong>}</td></tr>)}</tbody>
                  </table></div>
                  <div style={{ textAlign: 'right', marginTop: '12px' }}><button className="btn-search" onClick={() => setSeg(null)}>Fermer</button></div>
                </div>
              ) : seg.loading ? <div className="no-results">Chargement des {seg.by === 'ape' ? 'codes APE' : 'activités'}…</div>
                : seg.segments.length ? (
                  <div>
                    <p style={{ marginTop: 0, color: '#6b7280', fontSize: '14px' }}>Une liste Brevo sera créée pour chaque valeur cochée, avec les contacts correspondants.</p>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: 600 }}>
                      <input type="checkbox" checked={seg.segments.every(x => seg.selected.has(x.value))} onChange={toggleSegAll} /> Tout sélectionner ({seg.segments.length})
                    </label>
                    <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #e9e9f2', borderRadius: '8px', padding: '8px' }}>
                      {seg.segments.map(s => (
                        <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 4px', borderBottom: '1px solid #f3f4f6' }}>
                          <input type="checkbox" checked={seg.selected.has(s.value)} onChange={() => toggleSeg(s.value)} />
                          <span style={{ flex: 1 }}>{s.value}</span>
                          <span style={{ background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '10px', fontSize: '12px' }}>{s.n}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                      <button className="btn-close" style={{ position: 'static', fontSize: '14px', padding: '8px 16px' }} onClick={() => setSeg(null)} disabled={seg.running}>Annuler</button>
                      <button className="btn-search" style={{ background: '#7c3aed' }} onClick={runSegment} disabled={seg.running || seg.selected.size === 0}>{seg.running ? 'Création…' : `Créer ${seg.selected.size} liste(s)`}</button>
                    </div>
                  </div>
                ) : <div className="no-results">Aucune valeur de {seg.by === 'ape' ? 'code APE' : 'activité'} sur tes contacts. Importe un CSV avec une colonne {seg.by === 'ape' ? 'ape/naf' : 'categorie/secteur'}, ou renseigne-la à l'ajout.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ============ CHOIX EMAIL / SMS ============ */}
      {typeChoice && (
        <div className="modal-overlay" onClick={() => setTypeChoice(false)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '460px', width: '92vw' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 style={{ margin: 0, color: '#111827' }}>Nouvelle campagne</h3><button className="btn-close" onClick={() => setTypeChoice(false)}>&times;</button></div>
            <div style={{ padding: '18px', color: '#1f2937' }}>
              <p style={{ marginTop: 0, color: '#666', fontSize: '14px' }}>Quel type de campagne veux-tu créer ?</p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button onClick={() => { setTypeChoice(false); openEmailWizard(); }} style={{ flex: 1, minWidth: '170px', cursor: 'pointer', border: '2px solid #4f46e5', background: '#eef2ff', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                  <div style={{ fontSize: '34px' }}>📧</div>
                  <div style={{ fontWeight: 700, color: '#4338ca', marginTop: '6px' }}>Email</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Landing, suivi des ouvertures, leads chauds</div>
                </button>
                <button onClick={() => { setTypeChoice(false); openSmsForm(); }} style={{ flex: 1, minWidth: '170px', cursor: 'pointer', border: '2px solid #0d9488', background: '#ecfdf5', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                  <div style={{ fontSize: '34px' }}>📱</div>
                  <div style={{ fontWeight: 700, color: '#0f766e', marginTop: '6px' }}>SMS</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Message court vers les mobiles (crédits Brevo)</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL NOUVEAU SMS ============ */}
      {smsForm && (
        <div className="modal-overlay" onClick={() => !smsSaving && setSmsForm(null)}>
          <div className="modal-content planning-modal" style={{ maxWidth: '640px', width: '94vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 style={{ margin: 0, color: '#111827' }}>📱 Nouveau SMS</h3><button className="btn-close" onClick={() => !smsSaving && setSmsForm(null)}>&times;</button></div>
            <div style={{ padding: '16px', color: '#1f2937', display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div className="filter-group" style={{ flex: 2, minWidth: '180px', margin: 0 }}><label>Nom de la campagne SMS *</label><input className="filter-input" value={smsForm.name} onChange={e => setSmsForm({ ...smsForm, name: e.target.value })} placeholder="ex: SMS relance restaurants" /></div>
                <div className="filter-group" style={{ flex: 1, minWidth: '120px', margin: 0 }}><label>Expéditeur (11 car. max)</label><input className="filter-input" maxLength={11} value={smsForm.sender} onChange={e => setSmsForm({ ...smsForm, sender: e.target.value })} /></div>
              </div>
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Message * <span style={{ color: smsForm.content.length > 160 ? '#dc2626' : '#888', fontWeight: 400 }}>({smsForm.content.length} car. — {Math.max(1, Math.ceil(smsForm.content.length / 160))} SMS)</span></label>
                <textarea value={smsForm.content} onChange={e => setSmsForm({ ...smsForm, content: e.target.value })} style={{ minHeight: '110px', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '14px' }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
                  {['first_name', 'company', 'dirigeant', 'city', 'date_passage'].map(v => (
                    <button key={v} type="button" onClick={() => setSmsForm({ ...smsForm, content: smsForm.content + `{{${v}}}` })} style={{ border: 'none', cursor: 'pointer', background: '#eef2ff', color: '#4338ca', padding: '3px 8px', borderRadius: '10px', fontSize: '12px' }}>+ {v}</button>
                  ))}
                </div>
                <small style={{ color: '#888' }}>Conseil : termine par « STOP au XXXXX » (désabonnement) pour rester conforme.</small>
              </div>
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Cibler par activité</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '140px', overflowY: 'auto' }}>
                  {smsSeg.map(s => { const on = smsForm.activites.includes(s.value); return (
                    <button key={s.value} type="button" onClick={() => toggleSmsAct(s.value)} style={{ border: 'none', cursor: 'pointer', background: on ? '#4f46e5' : '#eef2ff', color: on ? '#fff' : '#4338ca', padding: '5px 10px', borderRadius: '14px', fontSize: '12px' }}>{on ? '✓ ' : ''}{s.value} ({s.n})</button>
                  ); })}
                </div>
                <div style={{ marginTop: '8px', fontSize: '14px', fontWeight: 600, color: '#065f46' }}>👥 {smsAud == null ? '…' : smsAud.toLocaleString('fr-FR')} ciblés — <span style={{ color: '#92400e' }}>seuls ceux avec un mobile (06/07) recevront le SMS</span></div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn-close" style={{ position: 'static', fontSize: '14px', padding: '8px 16px' }} onClick={() => createSms(false)} disabled={smsSaving}>Brouillon</button>
                <button className="btn-search" style={{ background: '#10b981' }} onClick={() => createSms(true)} disabled={smsSaving}>{smsSaving ? 'Envoi…' : '📱 Créer et envoyer'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {wizard && <Wizard w={wizard} setW={setWizard} pages={pages} lists={lists} loadPages={loadPages} loadLists={loadLists} loadCampaigns={loadCampaigns} loadStats={loadStats} flash={flash} onClose={() => setWizard(null)} />}
    </div>
  );
}

// ===================== ASSISTANT DE CAMPAGNE =====================
function Wizard({ w, setW, pages, lists, loadPages, loadLists, loadCampaigns, loadStats, flash, onClose }) {
  const set = (patch) => setW(prev => ({ ...prev, ...patch }));
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState('');
  const [segActs, setSegActs] = useState([]);
  const [segApes, setSegApes] = useState([]);
  const [audCount, setAudCount] = useState(null);
  const [singleKnown, setSingleKnown] = useState(null); // null=non vérifié, true=contact connu, false=inconnu
  const [singleContact, setSingleContact] = useState(null);

  const loadTemplates = () => axios.get(`${EM}/templates`, auth()).then(r => setTemplates(r.data || [])).catch(() => {});
  useEffect(() => {
    loadPages(); loadLists(); loadTemplates();
    axios.get(`${EM}/segments?by=activite`, auth()).then(r => setSegActs(r.data.segments || [])).catch(() => {});
    axios.get(`${EM}/segments?by=ape`, auth()).then(r => setSegApes(r.data.segments || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  // Comptage live de l'audience filtrée
  useEffect(() => {
    if (w.audienceMode !== 'filter') return;
    if (!(w.activites || []).length && !(w.apes || []).length) { setAudCount(0); return; }
    let cancel = false;
    axios.post(`${EM}/audience-count`, { activites: w.activites, apes: w.apes }, auth())
      .then(r => { if (!cancel) setAudCount(r.data.count); }).catch(() => {});
    return () => { cancel = true; };
  }, [w.audienceMode, w.activites, w.apes]);

  const toggleArr = (key, val) => set({ [key]: (w[key] || []).includes(val) ? w[key].filter(x => x !== val) : [...(w[key] || []), val] });

  // Vérifie si l'email (envoi unique) est déjà un contact connu
  useEffect(() => {
    if (w.audienceMode !== 'single') return;
    const em = (w.singleEmail || '').trim().toLowerCase();
    if (!/.+@.+\..+/.test(em)) { setSingleKnown(null); setSingleContact(null); return; }
    let cancel = false;
    const t = setTimeout(() => {
      axios.get(`${EM}/contact-lookup?email=${encodeURIComponent(em)}`, auth())
        .then(r => { if (!cancel) { setSingleKnown(r.data.found); setSingleContact(r.data.contact || null); } }).catch(() => {});
    }, 400);
    return () => { cancel = true; clearTimeout(t); };
  }, [w.singleEmail, w.audienceMode]);

  // ===== Éditeur visuel (WYSIWYG, sans code) =====
  const editorRef = useRef(null);
  const MSG_VARS = [
    { v: 'first_name', l: 'Prénom' }, { v: 'company', l: 'Établissement' }, { v: 'dirigeant', l: 'Dirigeant' },
    { v: 'city', l: 'Ville' }, { v: 'zipcode', l: 'CP' }, { v: 'siret', l: 'SIRET' },
    { v: 'date_passage', l: 'Date +10j' }, { v: 'landing_url', l: 'Lien landing' }
  ];
  const syncBody = () => { if (editorRef.current) set({ body: editorRef.current.innerHTML }); };
  // Init du contenu de l'éditeur quand on arrive à l'étape Message
  useEffect(() => {
    if (w.step === 3 && editorRef.current) editorRef.current.innerHTML = w.body || '';
    // eslint-disable-next-line
  }, [w.step]);
  const exec = (cmd, val) => { editorRef.current && editorRef.current.focus(); document.execCommand(cmd, false, val); syncBody(); };
  const insertHTML = (html) => { editorRef.current && editorRef.current.focus(); document.execCommand('insertHTML', false, html); syncBody(); };
  const setEditorHTML = (html) => { if (editorRef.current) editorRef.current.innerHTML = html; set({ body: html }); };
  const insertVarChip = (v) => insertHTML(`{{${v}}}`);
  const insertCtaButton = () => insertHTML(`<p style="text-align:center;margin:28px 0"><a href="{{landing_url}}" style="background:#4f46e5;color:#fff;padding:14px 28px;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block">Découvrir mon offre</a></p>`);
  const addLink = () => { const url = window.prompt('Adresse du lien (https://…) :', 'https://'); if (url) exec('createLink', url); };
  const tbBtn = { border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', padding: '4px 9px', borderRadius: '6px', fontSize: '13px', minWidth: '30px' };

  const applyTemplate = (id) => {
    setTplId(id);
    if (!id) return;
    const t = templates.find(x => String(x.id) === String(id));
    if (t) { set({ subject: t.subject || w.subject }); setEditorHTML(t.html_body); }
  };
  const saveAsTemplate = async () => {
    const name = window.prompt('Nom du modèle à enregistrer :', w.name || 'Mon modèle');
    if (!name) return;
    try { await axios.post(`${EM}/templates`, { name, subject: w.subject, html_body: w.body }, auth()); await loadTemplates(); flash('Modèle enregistré.'); }
    catch (e) { alert('Erreur enregistrement modèle'); }
  };

  const STEPS = ['Landing page', 'Contacts', 'Message', 'Envoi'];

  const next = async () => {
    if (w.step === 1 && w.landingMode === 'new' && !w.landingNew.title.trim()) { alert('Donnez un titre à la landing page.'); return; }
    if (w.step === 1 && w.landingMode === 'existing' && !w.landingId && pages.length) { /* ok, autorise sans landing */ }
    if (w.step === 2 && w.audienceMode === 'filter' && !(w.activites || []).length && !(w.apes || []).length) { alert('Sélectionnez au moins une activité ou un code APE.'); return; }
    if (w.step === 2 && w.audienceMode === 'single') {
      if (!/.+@.+\..+/.test(w.singleEmail || '')) { alert('Saisissez un email valide.'); return; }
      let known = singleKnown;
      if (known === null) {
        try { const r = await axios.get(`${EM}/contact-lookup?email=${encodeURIComponent(w.singleEmail.trim())}`, auth()); known = r.data.found; setSingleKnown(known); setSingleContact(r.data.contact || null); } catch (e) {}
      }
      if (known === false && (!(w.singleName || '').trim() || !(w.singlePhone || '').trim())) { alert('Cet email n\'est pas dans tes contacts : renseigne un nom et un téléphone.'); return; }
    }
    if (w.step === 2 && w.audienceMode === 'list' && !w.list) { alert('Choisissez une liste (ou importez un CSV).'); return; }
    if (w.step === 2 && w.audienceMode === 'import' && !w.importFile) { alert('Sélectionnez un fichier CSV.'); return; }
    if (w.step === 3 && (!w.name.trim() || !w.subject.trim() || !w.body.trim())) { alert('Nom, sujet et message requis.'); return; }
    set({ step: w.step + 1 });
  };
  const back = () => set({ step: w.step - 1 });

  const finish = async (sendNow) => {
    setBusy(true);
    try {
      // 1) Landing
      let landing_url = '';
      if (w.landingMode === 'existing' && w.landingId) landing_url = landingUrl(w.landingId);
      if (w.landingMode === 'new') {
        const fd = new FormData();
        const L = w.landingNew;
        fd.append('title', L.title); fd.append('business_name', L.business_name); fd.append('description', L.description);
        fd.append('button_text', L.button_text); fd.append('color_primary', L.color_primary); fd.append('color_secondary', L.color_secondary);
        fd.append('email_recipients', L.email_recipients); fd.append('fields', JSON.stringify(L.fields));
        const r = await axios.post(`${CEE}/pages`, fd, mp());
        if (r.data?.id) { landing_url = landingUrl(r.data.id); loadPages(); }
      }
      // 2) Audience
      let tags_filter = '';
      let activites = [], apes = [], single_email = '', single_name = '', single_phone = '';
      if (w.audienceMode === 'single') { single_email = (w.singleEmail || '').trim(); single_name = (w.singleName || '').trim(); single_phone = (w.singlePhone || '').trim(); }
      if (w.audienceMode === 'filter') { activites = w.activites || []; apes = w.apes || []; }
      if (w.audienceMode === 'list') tags_filter = (w.list === '__all__') ? '' : w.list;
      if (w.audienceMode === 'import' && w.importFile) {
        const listName = (w.importListName || '').trim() || ('liste-' + Date.now());
        const fd = new FormData(); fd.append('file', w.importFile); fd.append('list', listName);
        await axios.post(`${EM}/contacts/import`, fd, mp());
        tags_filter = listName; loadLists();
      }
      // 3) Campagne
      const r = await axios.post(`${EM}/campaigns`, {
        name: w.name, subject: w.subject, html_body: w.body, channel: w.channel,
        from_name: w.from_name, from_email: w.from_email, reply_to: w.reply_to,
        tags_filter, landing_url, activites, apes, single_email, single_name, single_phone
      }, auth());
      const campId = r.data.id;
      loadCampaigns(); loadStats();
      if (sendNow && campId) { await axios.post(`${EM}/campaigns/${campId}/send`, {}, auth()); flash('Campagne créée et envoi lancé ! Suivez les ouvertures dans Résultats.'); }
      else flash('Campagne enregistrée en brouillon.');
      onClose();
    } catch (e) { alert(e.response?.data?.error || 'Erreur lors de la création de la campagne'); }
    finally { setBusy(false); }
  };

  const lNew = w.landingNew;
  const setLNew = (patch) => set({ landingNew: { ...w.landingNew, ...patch } });

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal-content planning-modal" style={{ maxWidth: w.step === 3 ? '900px' : '720px', width: '95vw', maxHeight: '94vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ margin: 0, color: '#111827' }}>Nouvelle campagne — {STEPS[w.step - 1]}</h3><button className="btn-close" onClick={() => !busy && onClose()}>&times;</button></div>

        {/* Stepper */}
        <div style={{ display: 'flex', gap: '6px', padding: '12px 16px 0' }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ flex: 1, textAlign: 'center', fontSize: '12px', fontWeight: 600, color: w.step === i + 1 ? '#4f46e5' : w.step > i + 1 ? '#10b981' : '#bbb' }}>
              <div style={{ height: '4px', borderRadius: '4px', background: w.step >= i + 1 ? (w.step > i + 1 ? '#10b981' : '#4f46e5') : '#e5e7eb', marginBottom: '6px' }} />
              {w.step > i + 1 ? '✓ ' : ''}{s}
            </div>
          ))}
        </div>

        <div style={{ padding: '16px', color: '#1f2937' }}>
          {/* STEP 1 — LANDING */}
          {w.step === 1 && (
            <div>
              <p style={{ marginTop: 0, color: '#666', fontSize: '14px' }}>Vers quelle page votre email doit-il rediriger ?</p>
              {['existing', 'new', 'none'].map(m => (
                <label key={m} style={{ display: 'block', padding: '10px', border: `2px solid ${w.landingMode === m ? '#4f46e5' : '#e5e7eb'}`, borderRadius: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                  <input type="radio" checked={w.landingMode === m} onChange={() => set({ landingMode: m })} style={{ marginRight: '8px' }} />
                  <strong>{m === 'existing' ? 'Utiliser une landing existante' : m === 'new' ? 'Créer une nouvelle landing' : 'Aucune landing (email simple)'}</strong>
                  {m === 'existing' && w.landingMode === 'existing' && (
                    <div style={{ marginTop: '10px' }}>
                      <select className="filter-input" value={w.landingId} onChange={e => set({ landingId: e.target.value })}>
                        <option value="">— Choisir une page —</option>
                        {pages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                      </select>
                    </div>
                  )}
                  {m === 'new' && w.landingMode === 'new' && (
                    <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
                      <input className="filter-input" placeholder="Titre de la page *" value={lNew.title} onChange={e => setLNew({ title: e.target.value })} />
                      <input className="filter-input" placeholder="Nom de l'entreprise" value={lNew.business_name} onChange={e => setLNew({ business_name: e.target.value })} />
                      <textarea placeholder="Description / accroche" value={lNew.description} onChange={e => setLNew({ description: e.target.value })} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '14px' }} />
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <label style={{ fontSize: '13px' }}>Couleurs :</label>
                        <input type="color" value={lNew.color_primary} onChange={e => setLNew({ color_primary: e.target.value })} />
                        <input type="color" value={lNew.color_secondary} onChange={e => setLNew({ color_secondary: e.target.value })} />
                        <input className="filter-input" style={{ flex: 1 }} placeholder="Texte du bouton" value={lNew.button_text} onChange={e => setLNew({ button_text: e.target.value })} />
                      </div>
                      <small style={{ color: '#888' }}>Champs du formulaire par défaut : Nom, Email, Téléphone.</small>
                    </div>
                  )}
                </label>
              ))}
            </div>
          )}

          {/* STEP 2 — CONTACTS */}
          {w.step === 2 && (
            <div>
              <p style={{ marginTop: 0, color: '#666', fontSize: '14px' }}>À qui envoyer cette campagne ?</p>

              {/* Mode : à une seule personne */}
              <label style={{ display: 'block', padding: '10px', border: `2px solid ${w.audienceMode === 'single' ? '#4f46e5' : '#e5e7eb'}`, borderRadius: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                <input type="radio" checked={w.audienceMode === 'single'} onChange={() => set({ audienceMode: 'single' })} style={{ marginRight: '8px' }} />
                <strong>À une seule personne</strong> <span style={{ color: '#888', fontSize: '13px' }}>(envoi unique / test)</span>
                {w.audienceMode === 'single' && (
                  <div style={{ marginTop: '10px' }} onClick={e => e.preventDefault()}>
                    <input className="filter-input" type="email" value={w.singleEmail || ''} onChange={e => set({ singleEmail: e.target.value })} placeholder="email@destinataire.fr" />
                    {singleKnown === true && (
                      <div style={{ marginTop: '8px', color: '#065f46', fontSize: '13px', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '8px 10px' }}>
                        ✓ Contact connu{singleContact && (singleContact.company || singleContact.dirigeant) ? ` : ${singleContact.company || singleContact.dirigeant}` : ''}{singleContact && singleContact.phone ? ` — ${singleContact.phone}` : ''}. Ses infos personnalisent le message.
                      </div>
                    )}
                    {singleKnown === false && (
                      <div style={{ marginTop: '8px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '8px', padding: '10px' }}>
                        <div style={{ color: '#9a3412', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>⚠️ Email pas dans tes contacts → nom + téléphone obligatoires (pour le retrouver dans Résultats)</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <input className="filter-input" style={{ flex: 1, minWidth: '160px' }} value={w.singleName || ''} onChange={e => set({ singleName: e.target.value })} placeholder="Nom (établissement ou dirigeant) *" />
                          <input className="filter-input" style={{ flex: 1, minWidth: '140px' }} value={w.singlePhone || ''} onChange={e => set({ singlePhone: e.target.value })} placeholder="Téléphone *" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </label>

              {/* Mode : filtrer par activité / APE */}
              <label style={{ display: 'block', padding: '10px', border: `2px solid ${w.audienceMode === 'filter' ? '#4f46e5' : '#e5e7eb'}`, borderRadius: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                <input type="radio" checked={w.audienceMode === 'filter'} onChange={() => set({ audienceMode: 'filter' })} style={{ marginRight: '8px' }} />
                <strong>Cibler par activité et/ou code APE</strong>
                {w.audienceMode === 'filter' && (
                  <div style={{ marginTop: '10px' }} onClick={e => e.preventDefault()}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#4338ca', margin: '0 0 6px' }}>Activités</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '150px', overflowY: 'auto' }}>
                      {segActs.length ? segActs.map(s => {
                        const on = (w.activites || []).includes(s.value);
                        return <button key={s.value} type="button" onClick={() => toggleArr('activites', s.value)} style={{ border: 'none', cursor: 'pointer', background: on ? '#4f46e5' : '#eef2ff', color: on ? '#fff' : '#4338ca', padding: '5px 10px', borderRadius: '14px', fontSize: '12px' }}>{on ? '✓ ' : ''}{s.value} ({s.n})</button>;
                      }) : <span style={{ color: '#999', fontSize: '13px' }}>Aucune activité (importe des contacts avec une colonne activité).</span>}
                    </div>
                    {segApes.length > 0 && <>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#4338ca', margin: '12px 0 6px' }}>Codes APE</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
                        {segApes.map(s => {
                          const on = (w.apes || []).includes(s.value);
                          return <button key={s.value} type="button" onClick={() => toggleArr('apes', s.value)} style={{ border: 'none', cursor: 'pointer', background: on ? '#7c3aed' : '#f3e8ff', color: on ? '#fff' : '#6d28d9', padding: '5px 10px', borderRadius: '14px', fontSize: '12px' }}>{on ? '✓ ' : ''}{s.value} ({s.n})</button>;
                        })}
                      </div>
                    </>}
                    <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 600, color: '#065f46' }}>
                      👥 {audCount == null ? '…' : audCount.toLocaleString('fr-FR')} contact(s) ciblé(s)
                    </div>
                  </div>
                )}
              </label>

              <label style={{ display: 'block', padding: '10px', border: `2px solid ${w.audienceMode === 'list' ? '#4f46e5' : '#e5e7eb'}`, borderRadius: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                <input type="radio" checked={w.audienceMode === 'list'} onChange={() => set({ audienceMode: 'list' })} style={{ marginRight: '8px' }} />
                <strong>Choisir une liste existante</strong>
                {w.audienceMode === 'list' && (
                  <div style={{ marginTop: '10px' }}>
                    <select className="filter-input" value={w.list} onChange={e => set({ list: e.target.value })}>
                      <option value="">— Choisir une liste —</option>
                      <option value="__all__">Tous les contacts</option>
                      {lists.map(l => <option key={l.list} value={l.list}>{l.list} ({l.n})</option>)}
                    </select>
                  </div>
                )}
              </label>
              <label style={{ display: 'block', padding: '10px', border: `2px solid ${w.audienceMode === 'import' ? '#4f46e5' : '#e5e7eb'}`, borderRadius: '10px', cursor: 'pointer' }}>
                <input type="radio" checked={w.audienceMode === 'import'} onChange={() => set({ audienceMode: 'import' })} style={{ marginRight: '8px' }} />
                <strong>Importer un nouveau CSV</strong>
                {w.audienceMode === 'import' && (
                  <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
                    <input type="file" accept=".csv" onChange={e => set({ importFile: e.target.files[0] || null })} />
                    <input className="filter-input" placeholder="Nom de la liste (optionnel)" value={w.importListName} onChange={e => set({ importListName: e.target.value })} />
                    <small style={{ color: '#888' }}>Colonnes : email, prenom, nom, societe, tel, siret, adresse, ville, cp, categorie, dirigeant.</small>
                  </div>
                )}
              </label>
            </div>
          )}

          {/* STEP 3 — MESSAGE */}
          {w.step === 3 && (
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', background: '#f7f7fb', border: '1px solid #e3e3ef', borderRadius: '10px', padding: '10px' }}>
                <div className="filter-group" style={{ margin: 0, flex: 1, minWidth: '200px' }}>
                  <label>Partir d'un modèle</label>
                  <select className="filter-input" value={tplId} onChange={e => applyTemplate(e.target.value)}>
                    <option value="">— Partir de zéro —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <button type="button" className="btn-voir-fiche" onClick={saveAsTemplate} title="Enregistrer le message actuel comme modèle">💾 Enregistrer comme modèle</button>
              </div>
              <div className="filter-group" style={{ margin: 0 }}><label>Nom de la campagne *</label><input className="filter-input" value={w.name} onChange={e => set({ name: e.target.value })} placeholder="ex: Relance restaurants juin" /></div>
              <div className="filter-group" style={{ margin: 0 }}><label>Sujet de l'email *</label><input className="filter-input" value={w.subject} onChange={e => set({ subject: e.target.value })} /></div>

              {/* Message — éditeur visuel (on écrit directement, sans code) */}
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Message — écrivez directement ci-dessous</label>
                {/* Barre d'outils */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', background: '#f3f4f6', border: '1px solid #d1d5db', borderBottom: 'none', borderRadius: '8px 8px 0 0', padding: '8px' }}>
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('bold')} style={tbBtn} title="Gras"><b>G</b></button>
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('italic')} style={tbBtn} title="Italique"><i>I</i></button>
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('underline')} style={tbBtn} title="Souligné"><u>S</u></button>
                  <label style={{ ...tbBtn, display: 'inline-flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }} title="Couleur du texte" onMouseDown={e => e.preventDefault()}>
                    🎨<input type="color" onChange={e => exec('foreColor', e.target.value)} style={{ width: '0', height: '0', opacity: 0, position: 'absolute' }} />
                  </label>
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={addLink} style={tbBtn} title="Insérer un lien">🔗</button>
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('insertUnorderedList')} style={tbBtn} title="Liste à puces">• Liste</button>
                  <span style={{ width: '1px', height: '20px', background: '#cbd5e1', margin: '0 2px' }} />
                  {MSG_VARS.map(mv => (
                    <button key={mv.v} type="button" onMouseDown={e => e.preventDefault()} onClick={() => insertVarChip(mv.v)} title={`{{${mv.v}}}`}
                      style={{ border: 'none', cursor: 'pointer', background: '#eef2ff', color: '#4338ca', padding: '4px 9px', borderRadius: '12px', fontSize: '12px' }}>+ {mv.l}</button>
                  ))}
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={insertCtaButton} style={{ border: 'none', cursor: 'pointer', background: '#ede9fe', color: '#6d28d9', padding: '4px 9px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>+ 🔘 Bouton</button>
                </div>
                {/* Zone éditable = l'aperçu lui-même */}
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncBody}
                  onBlur={syncBody}
                  style={{ minHeight: '320px', maxHeight: '50vh', overflowY: 'auto', padding: '16px', border: '1px solid #d1d5db', borderRadius: '0 0 8px 8px', background: '#fff', color: '#1f2937', fontFamily: 'Arial,sans-serif', fontSize: '15px', lineHeight: 1.6, outline: 'none' }}
                />
                <small style={{ color: '#888' }}>Écrivez comme dans un traitement de texte. Les boutons « + Prénom », « + Établissement »… insèrent des champs qui seront remplacés par les infos de chaque contact à l'envoi. Le suivi d'ouverture est ajouté automatiquement.</small>
              </div>
            </div>
          )}

          {/* STEP 4 — ENVOI */}
          {w.step === 4 && (
            <div style={{ display: 'grid', gap: '12px' }}>
              <div className="filter-group" style={{ margin: 0 }}>
                <label>Provider d'envoi</label>
                <select className="filter-input" value={w.channel} onChange={e => set({ channel: e.target.value })}>
                  {CHANNELS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div className="filter-group" style={{ flex: 1, minWidth: '180px', margin: 0 }}><label>Nom expéditeur</label><input className="filter-input" value={w.from_name} onChange={e => set({ from_name: e.target.value })} placeholder="(défaut Brevo)" /></div>
                <div className="filter-group" style={{ flex: 1, minWidth: '180px', margin: 0 }}><label>Email expéditeur</label><input className="filter-input" value={w.from_email} onChange={e => set({ from_email: e.target.value })} placeholder="(défaut Brevo, doit être vérifié)" /></div>
              </div>
              <div className="filter-group" style={{ margin: 0 }}><label>Répondre à</label><input className="filter-input" value={w.reply_to} onChange={e => set({ reply_to: e.target.value })} /></div>
              <div style={{ background: '#f7f7fb', border: '1px solid #e3e3ef', borderRadius: '10px', padding: '12px', fontSize: '14px' }}>
                <strong>Récapitulatif</strong><br />
                Landing : {w.landingMode === 'existing' ? (w.landingId ? 'page existante' : 'aucune') : w.landingMode === 'new' ? 'nouvelle (' + (w.landingNew.title || '?') + ')' : 'aucune'}<br />
                Audience : {w.audienceMode === 'single' ? `1 personne (${w.singleEmail || '?'})` : w.audienceMode === 'filter' ? `${[...(w.activites || []), ...(w.apes || [])].join(', ') || '?'} — ${audCount == null ? '…' : audCount} contact(s)` : w.audienceMode === 'list' ? (w.list === '__all__' ? 'tous les contacts' : 'liste « ' + (w.list || '?') + ' »') : 'import CSV (' + (w.importFile?.name || '?') + ')'}<br />
                Provider : {CHANNELS.find(c => c.v === w.channel)?.l}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '0 16px 16px' }}>
          <button className="btn-close" style={{ position: 'static', fontSize: '14px', padding: '8px 16px', visibility: w.step > 1 ? 'visible' : 'hidden' }} onClick={back} disabled={busy}>← Retour</button>
          {w.step < 4 ? (
            <button className="btn-search" onClick={next} disabled={busy}>Suivant →</button>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-close" style={{ position: 'static', fontSize: '14px', padding: '8px 16px' }} onClick={() => finish(false)} disabled={busy}>Enregistrer (brouillon)</button>
              <button className="btn-search" style={{ background: '#10b981' }} onClick={() => finish(true)} disabled={busy}>{busy ? 'Envoi…' : '🚀 Créer et envoyer'}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompagneCEE;
